import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Typography } from "@mui/material";
import { PersonRemoveOutlined } from "@mui/icons-material";
import PasswordIcon from "@mui/icons-material/Password";
import { ConfirmDialog } from "@outpave/ds";
import useUserProfileData from "hooks/useUserProfileData";
import SendIcon from "@mui/icons-material/Send";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";

/**
 * The brand-coloured email inside each prompt. `ConfirmDialog` does no i18n of
 * its own, so every title here passes through `t()` at the call site: the
 * ConfirmModal these replaced translated its title internally, and dropping
 * that would have reverted all four to English outside en-US.
 */
const EmailHighlight = (
  <Typography
    component="span"
    variant="inherit"
    sx={{ color: "var(--color-text-brand)" }}
  />
);

const errorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

interface RemoveUserModalProps {
  openRemoveUserModal: boolean;
  setOpenRemoveUserModal: (open: boolean) => void;
  userId: string;
  userEmail: string;
  handleUpdateUser?: () => void;
}

export const RemoveUserModal: React.FC<RemoveUserModalProps> = ({
  openRemoveUserModal,
  setOpenRemoveUserModal,
  userId,
  userEmail,
  handleUpdateUser,
}) => {
  const { handleRemoveUser, specificLoadingStates } = useUserProfileData({});
  const { t } = useTranslation();

  return (
    <>
      <ConfirmDialog
        open={openRemoveUserModal}
        tone="danger"
        title={t("Remove User")}
        busy={specificLoadingStates.removingUser}
        icon={<PersonRemoveOutlined />}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={async () => {
          try {
            setOpenRemoveUserModal(false);
            await handleRemoveUser(userId);
            handleUpdateUser && handleUpdateUser();
            toast.success(t("Successfully removed user"));
          } catch (err) {
            console.error(err);
            toast.error(errorMessage(err, t("Failed to remove user")));
          }
        }}
        onCancel={() => {
          if (specificLoadingStates.removingUser) return;
          setOpenRemoveUserModal(false);
        }}
        message={
          <Trans
            i18nKey="removeUserConfirmation"
            components={{ user_email: EmailHighlight }}
            values={{ email: userEmail }}
          />
        }
      />
      <LoadingWithoutModal open={specificLoadingStates.removingUser} />
    </>
  );
};

interface ResetUserPasswordModalProps {
  openResetUserPasswordModal: boolean;
  setOpenResetUserPasswordModal: (open: boolean) => void;
  userEmail: string;
}

export const ResetUserPasswordModal: React.FC<ResetUserPasswordModalProps> = ({
  openResetUserPasswordModal,
  setOpenResetUserPasswordModal,
  userEmail,
}) => {
  const { resetUserPassword, specificLoadingStates } = useUserProfileData({});
  const { t } = useTranslation();

  return (
    <>
      <ConfirmDialog
        tone="primary"
        title={t("Reset User Password")}
        busy={specificLoadingStates.resettingUserPassword}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        message={
          <Trans
            i18nKey="Reset the Outpave user password for <user_email>{{ user_email }}</user_email>?"
            values={{ user_email: userEmail }}
            components={{ user_email: EmailHighlight }}
          />
        }
        icon={<PasswordIcon />}
        open={openResetUserPasswordModal}
        onCancel={() => {
          if (specificLoadingStates.resettingUserPassword) return;
          setOpenResetUserPasswordModal(false);
        }}
        onConfirm={async () => {
          try {
            setOpenResetUserPasswordModal(false);
            await resetUserPassword(userEmail);
            toast.success(t("Reset user password email sent successfully"));
          } catch (err) {
            console.error(err);
            toast.error(t("Failed to send reset user password email"));
          }
        }}
      />
      <LoadingWithoutModal open={specificLoadingStates.resettingUserPassword} />
    </>
  );
};

interface ResendUserInviteModalProps {
  openResendUserInviteModal: boolean;
  setOpenResendUserInviteModal: (open: boolean) => void;
  userEmail: string;
  handleUpdateUser?: () => void;
}

export const ResendUserInviteModal: React.FC<ResendUserInviteModalProps> = ({
  openResendUserInviteModal,
  setOpenResendUserInviteModal,
  userEmail,
  handleUpdateUser,
}) => {
  const { specificLoadingStates, handleResendUserInviteEmail } =
    useUserProfileData({});
  const { t } = useTranslation();

  return (
    <>
      <ConfirmDialog
        tone="primary"
        title={t("Resend Invite")}
        busy={specificLoadingStates.resendingUserInvite}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        message={
          <Trans
            i18nKey="Resend the Outpave user invite to <user_email>{{ user_email }}</user_email>?"
            values={{ user_email: userEmail }}
            components={{ user_email: EmailHighlight }}
          />
        }
        icon={<SendIcon />}
        open={openResendUserInviteModal}
        onCancel={() => {
          if (specificLoadingStates.resendingUserInvite) return;
          setOpenResendUserInviteModal(false);
        }}
        onConfirm={async () => {
          try {
            await handleResendUserInviteEmail(userEmail);
            handleUpdateUser && handleUpdateUser();
            setOpenResendUserInviteModal(false);
            toast.success(t("Invite resent successfully"));
          } catch (err) {
            console.error(err);
            toast.error(t("Failed to resend invite"));
          }
        }}
      />
      <LoadingWithoutModal open={specificLoadingStates.resendingUserInvite} />
    </>
  );
};

interface SendUserBankAccountInviteModalProps {
  openSendBankAccountInviteModal: boolean;
  setOpenSendBankAccountInviteModal: (open: boolean) => void;
  userEmail: string;
  userId: string;
}

export const SendUserBankAccountInviteModal: React.FC<
  SendUserBankAccountInviteModalProps
> = ({
  openSendBankAccountInviteModal,
  setOpenSendBankAccountInviteModal,
  userEmail,
  userId,
}) => {
  const { handleSendBankAccountInvite, specificLoadingStates } =
    useUserProfileData({});
  const { t } = useTranslation();

  return (
    <>
      <ConfirmDialog
        tone="primary"
        title={t("Send Personal Bank Account Setup Invitation")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        message={
          <Trans
            i18nKey="SendPersonalInvitationDescription"
            values={{ user_email: userEmail }}
            components={{ user_email: EmailHighlight }}
          />
        }
        icon={<AccountBalanceIcon />}
        open={openSendBankAccountInviteModal}
        busy={specificLoadingStates.sendingBankAccountInvite}
        onCancel={() => {
          if (specificLoadingStates.sendingBankAccountInvite) return;
          setOpenSendBankAccountInviteModal(false);
        }}
        onConfirm={async () => {
          try {
            await handleSendBankAccountInvite(userId);

            setOpenSendBankAccountInviteModal(false);
            toast.success(t("BankAccountInviteSent"));
          } catch (err) {
            console.error(err);
            toast.error(t("BankAccountInviteError"));
          }
        }}
      />
      <LoadingWithoutModal
        open={specificLoadingStates.sendingBankAccountInvite}
      />
    </>
  );
};
